import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Bot, LineChart, LayoutDashboard, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../lib/api";

const modules = [
  {
    id: "smartevaluate",
    title: "SmartEvaluate",
    description: "Evaluate your warehouse operations and identify the right SAP strategy with AI-driven insights",
    icon: Bot,
    status: "Active",
    route: "/evaluate",
    cta: "Start Evaluation",
    active: true,
  },
  {
    id: "smartimprove",
    title: "SmartImprove",
    description: "Identify gaps and optimize warehouse performance for your existing warehouse issues",
    icon: LineChart,
    status: "Active",
    route: "/improve",
    cta: "Start Improvement",
    active: true,
  },
  {
    id: "smartblueprint",
    title: "SmartBlueprint",
    description: "Map processes, identify fit–gap, and build a detailed implementation roadmap.",
    icon: LayoutDashboard,
    status: "Coming Soon",
    route: null,
    active: false,
  },
];

export const ModuleCards = ({ userEmail }) => {
  const navigate = useNavigate();

  const handleCardClick = (mod) => {
    trackEvent(`module_card_click_${mod.id}`, userEmail);
    if (mod.active && mod.route) {
      navigate(mod.route);
    }
  };

  return (
    <section data-testid="module-cards-section" className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2
          className="text-2xl sm:text-3xl font-semibold text-[#111827] tracking-tight mb-2"
          style={{ fontFamily: 'Chivo, sans-serif' }}
        >
          Platform Modules
        </h2>
        <p className="text-sm text-[#6B7280] mb-8">
          Three connected modules, built to work together across the warehouse lifecycle.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {modules.map((mod) => (
            <Card
              key={mod.id}
              data-testid={`module-card-${mod.id}`}
              className={`relative overflow-hidden ${
                mod.active
                  ? "bg-white border border-[#E5E7EB] border-t-4 border-t-[#025979] hover:border-[#025979]/60 hover:shadow-md shadow-sm cursor-pointer transition-all duration-200"
                  : "bg-gray-50 border border-[#E5E7EB] opacity-75 transition-colors"
              } rounded-sm`}
              onClick={() => mod.active && handleCardClick(mod)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                      mod.active ? "bg-[#E0F2FE]" : "bg-gray-100"
                    }`}
                  >
                    <mod.icon
                      className={`w-5 h-5 ${mod.active ? "text-[#025979]" : "text-[#6B7280]"}`}
                      strokeWidth={1.5}
                    />
                  </div>
                  <Badge
                    data-testid={`module-badge-${mod.id}`}
                    variant={mod.active ? "default" : "secondary"}
                    className={`text-xs rounded-sm ${
                      mod.active
                        ? "bg-[#025979]/10 text-[#025979] hover:bg-[#025979]/10"
                        : "bg-gray-200 text-[#6B7280] hover:bg-gray-200"
                    }`}
                  >
                    {mod.status}
                  </Badge>
                </div>
                <CardTitle className="text-lg font-semibold text-[#111827]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {mod.title}
                </CardTitle>
                <CardDescription className="text-sm text-[#6B7280] mt-1.5 leading-relaxed">
                  {mod.description}
                </CardDescription>
              </CardHeader>
              <CardFooter className="pt-0">
                <Button
                  data-testid={`module-btn-${mod.id}`}
                  disabled={!mod.active}
                  variant={mod.active ? "default" : "secondary"}
                  className={`w-full rounded-sm text-sm font-medium ${
                    mod.active
                      ? "bg-[#025979] hover:bg-[#03738C] text-white shadow-sm"
                      : "bg-gray-200 text-[#6B7280] cursor-not-allowed"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(mod);
                  }}
                >
                  {mod.active ? (
                    <>
                      {mod.cta || "Start"}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  ) : (
                    "Coming Soon"
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
